import { AddonKey, Currency, Frequency, Status } from '@vendasta/marketplace-packages';
import { CONTACT_SALES } from '@vendasta/store';

export class Package {
  solution_id: string;
  partner_id: string;
  market_id: string;

  name: string;
  icon: string;
  status: string;
  tagline: string;
  header_image_url: string;

  pricing: Pricing;

  content: string;
  products: string[];
  addon_keys: AddonKey[];
  product_order: string[];

  hide_product_icons_and_names: boolean;
  hide_product_details: boolean;

  createdDate: Date;
  updatedDate: Date;

  get productOrder(): string[] {
    return this.product_order;
  }

  static fromApi(data: any): Package {
    if (!data) {
      return null;
    }
    const newPackage = new Package();
    newPackage.solution_id = data.packageId || data.solution_id;
    newPackage.partner_id = data.partnerId || data.partner_id;
    newPackage.market_id = data.marketId || data.market_id;
    newPackage.name = data.name || 'Untitled';
    newPackage.icon = data.icon;
    newPackage.status = Package.statusFrom(data);
    newPackage.tagline = data.tagline;
    newPackage.header_image_url = data.headerImageUrl || data.header_image_url;
    newPackage.pricing = Package.pricingFrom(data);
    newPackage.content = data.content;
    newPackage.products = data.products;
    newPackage.hide_product_icons_and_names = data.hideProductIconsAndNames || data.hide_product_icons_and_names;
    newPackage.hide_product_details = data.hideProductDetails || data.hide_product_details;
    newPackage.addon_keys = data.addon_keys || data.addonKeys;
    newPackage.createdDate = Package.createdDateFrom(data);
    newPackage.updatedDate = Package.updatedDateFrom(data);
    newPackage.product_order = data.product_order || data.productOrder;
    return newPackage;
  }

  static createdDateFrom(data: any): Date {
    if (data.created) {
      return new Date(data.created);
    }
  }

  static updatedDateFrom(data: any): Date {
    if (data.updated) {
      return new Date(data.updated);
    }
  }

  static pricingFrom(data: any): Pricing {
    const pricing: Pricing = {
      currency: Currency[Currency.USD],
      prices: [],
    };

    if (!data.pricing) {
      if (data.selling_price) {
        // handle the legacy case
        pricing.currency = data.currency;
        pricing.prices = [{ price: data.selling_price * 100, frequency: data.billing_frequency }];
      }
      return pricing;
    }

    if (data.pricing.currency) {
      if (typeof data.pricing.currency === 'string') {
        pricing.currency = data.pricing.currency;
      } else {
        pricing.currency = Currency[data.pricing.currency];
      }
    }

    (data.pricing.prices || []).map((price) => {
      const frequency =
        typeof price.frequency === 'string' ? price.frequency : Frequency[price.frequency || Frequency.MONTHLY];
      const newPrice: Price = {
        price: price.price || 0,
        frequency: this.capitalize(frequency),
        isStartingPrice: price.isStartingPrice,
      };
      pricing.prices.push(newPrice);
    });
    return pricing;
  }

  static statusFrom(data: any): string {
    if (!data.status) {
      return Status[Status.DRAFT];
    } else if (typeof data.status === 'string') {
      return data.status.toUpperCase();
    } else {
      return Status[data.status];
    }
  }

  get isPackageFree(): boolean {
    return this.pricing && this.pricing.prices
      ? this.pricing.prices.some((p) => p.price === 0 || p.price === undefined)
      : false;
  }

  get shouldContactSales(): boolean {
    return this.pricing && this.pricing.prices
      ? this.pricing.prices.some((p) => p.price === null || p.price <= CONTACT_SALES) ||
          this.pricing.prices.length === 0
      : true;
  }

  get formattedPricingList(): string[] {
    if (!this.pricing || !this.pricing.prices || this.pricing.prices.length === 0) {
      return ['Contact Sales'];
    }
    const hasCurrencyAndIsNotUSD = this.pricing.currency && this.pricing.currency !== 'USD';
    const currencyStr = hasCurrencyAndIsNotUSD ? this.pricing.currency : '';
    const currencySymbol = Package.getCurrencySymbol(this.pricing.currency);

    return this.pricing.prices.map((price) => {
      const frequencyStr = price.frequency ? '/ ' + price.frequency : '';
      const roundedPrice = (price.price / 100.0).toFixed(2);
      const startingAtPrefix = price.isStartingPrice ? 'Starting at ' : '';
      const priceStr = `${startingAtPrefix}${currencySymbol}${roundedPrice} ${currencyStr} ${frequencyStr}`;
      return priceStr.trim().replace(/\s+/g, ' ');
    });
  }

  get priceStrings(): string[] {
    if (this.shouldContactSales) {
      return ['Contact Sales'];
    }

    if (this.isPackageFree) {
      return ['Free'];
    }

    return this.formattedPricingList;
  }

  get formattedWholesalePrice(): string {
    return this.priceStrings.join(' + ');
  }

  static capitalize(inputString: string): string {
    if (typeof inputString !== 'string') {
      return inputString;
    }
    return inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase();
  }

  static getCurrencySymbol(currency: string): string {
    return (
      {
        EUR: '\u20AC',
        GBP: '\u00A3',
        CHF: 'Fr',
        CNY: '\u00A5',
        JPY: '\u00A5',
        CZK: 'K\u010d',
        INR: '\u20B9',
        KHR: '\u17DB',
        KRW: '\u20A9',
        NOK: 'kr',
        SEK: 'kr',
        RUB: '\u20BD',
        TRY: '\u20BA',
        ZAR: 'R',
      }[currency] || '$'
    );
  }
}

export interface Pricing {
  currency: string;
  prices: Price[];
}

export interface Price {
  price: number;
  frequency: string;
  isStartingPrice?: boolean;
}
